import React from "react";
import { usePhysicsStore } from "../../../stores/physicsStore";
import {
  useActuators,
  useBodies,
  useJoints,
  useWorldBodies,
  useSceneActions,
  useTendons,
} from "../../../stores/sceneStore";
import { buildBodyRootMap } from "../../../utils/bodyHierarchy";
import {
  generateActuatorControlName,
  generateStableActuatorNameFromUuid,
} from "../../../utils/actuatorIds";
import { resolveJointUuidFromRef } from "../../../utils/jointUtils";
import { useSelectionStore } from "../../../stores/selectionStore";
import { SPEED_ACTUATOR_KINDS } from "./constants";

export const useActuatorControlPanelData = () => {
  const sceneActions = useSceneActions();
  const actuators = useActuators();
  const bodies = useBodies();
  const worldBodies = useWorldBodies();
  const joints = useJoints();
  const tendons = useTendons();
  const selectedJoint = useSelectionStore((s) => s.selectedJoint);

  const motorValues = usePhysicsStore((s) => s.actuatorMotorControls);
  const posValues = usePhysicsStore((s) => s.actuatorPositionTargets);
  const {
    setActuatorPositionTarget,
    mergeActuatorPositionTargets,
    setActuatorMotorControl,
    resetActuatorControls,
  } = usePhysicsStore((s) => s.actions);

  const actuatorList = React.useMemo(() => Object.values(actuators || {}), [actuators]);

  const bodyRootMap = React.useMemo(
    () => buildBodyRootMap(bodies || {}, worldBodies || []),
    [bodies, worldBodies]
  );

  const jointToBodyMap = React.useMemo(() => {
    const map = {};
    Object.values(bodies || {}).forEach((body) => {
      if (!body || !Array.isArray(body.joints)) return;
      body.joints.forEach((jointRef) => {
        if (typeof jointRef === "string") {
          map[jointRef] = body.uuid;
        } else if (jointRef && typeof jointRef === "object" && jointRef.uuid) {
          map[jointRef.uuid] = body.uuid;
        }
      });
    });
    return map;
  }, [bodies]);

  const jointMetaMap = React.useMemo(() => {
    const map = new Map();
    Object.values(joints || {}).forEach((joint) => {
      if (!joint) return;
      const bodyUuid = jointToBodyMap[joint.uuid] || null;
      const rootUuid = bodyUuid ? bodyRootMap[bodyUuid] || bodyUuid : null;
      map.set(joint.uuid, { joint, bodyUuid, rootUuid });
    });
    return map;
  }, [joints, jointToBodyMap, bodyRootMap]);

  const resolveJointUuid = React.useCallback(
    (actuator) => {
      const jointRef = actuator?.joint;
      if (!jointRef || typeof jointRef !== "string") return null;
      return resolveJointUuidFromRef(jointRef, joints);
    },
    [joints]
  );

  const tendonRootResolver = React.useMemo(() => {
    const rootsByUuid = new Map();
    const rootsByName = new Map();

    Object.entries(tendons || {}).forEach(([tendonUuid, tendon]) => {
      if (!tendon || !Array.isArray(tendon.joints)) return;
      const member = tendon.joints.find((ref) => ref?.joint && jointMetaMap.has(ref.joint));
      const rootUuid = member ? jointMetaMap.get(member.joint)?.rootUuid : null;
      if (!rootUuid) return;
      rootsByUuid.set(tendonUuid, rootUuid);
      const name = typeof tendon.name === "string" ? tendon.name : "";
      if (!name) return;
      const existing = rootsByName.get(name);
      if (existing === undefined) {
        rootsByName.set(name, rootUuid);
      } else if (existing !== rootUuid) {
        rootsByName.set(name, null);
      }
    });

    return (actuator) => {
      const tendonUuid = actuator?.tendonUuid;
      if (typeof tendonUuid === "string" && rootsByUuid.has(tendonUuid)) {
        return rootsByUuid.get(tendonUuid);
      }
      const tendonName = actuator?.tendon;
      if (typeof tendonName === "string" && rootsByName.has(tendonName)) {
        return rootsByName.get(tendonName);
      }
      return null;
    };
  }, [tendons, jointMetaMap]);

  const getActuatorControlName = React.useCallback(
    (actuator) => {
      if (actuator?.uuid) {
        return generateStableActuatorNameFromUuid(actuator.uuid);
      }
      const jointUuid = resolveJointUuid(actuator);
      const jointMeta = jointUuid ? jointMetaMap.get(jointUuid) : null;
      const rootUuid = jointMeta?.rootUuid || null;
      const baseName =
        actuator?.name ||
        jointMeta?.joint?.name ||
        (jointUuid && joints[jointUuid]?.name) ||
        actuator?.tendon ||
        "actuator";
      return generateActuatorControlName({
        baseName,
        jointUuid,
        rootBodyUuid: rootUuid,
        tendonName: actuator?.tendon || null,
      });
    },
    [jointMetaMap, resolveJointUuid, joints]
  );

  const getActuatorStateKey = React.useCallback(
    (actuator) => getActuatorControlName(actuator),
    [getActuatorControlName]
  );

  React.useEffect(() => {
    if (actuatorList.length === 0) return;
    if (Object.keys(posValues || {}).length > 0) return;
    const initialPosValues = {};
    actuatorList.forEach((actuator) => {
      if (!actuator?.joint) return;
      const jointUuid = resolveJointUuidFromRef(actuator.joint, joints);
      const jointEntry = jointUuid ? joints[jointUuid] : null;
      if (!jointEntry) return;
      const jointType = (actuator.kind || "").toLowerCase();
      if (jointType === "position" || jointType === "intposition" || jointType === "general") {
        const stateKey = getActuatorStateKey(actuator);
        if (stateKey) {
          initialPosValues[stateKey] = jointEntry.ref ?? 0;
        }
      }
    });
    if (Object.keys(initialPosValues).length > 0) {
      mergeActuatorPositionTargets(initialPosValues);
    }
  }, [actuatorList, joints, getActuatorStateKey, mergeActuatorPositionTargets, posValues]);

  React.useEffect(() => {
    const handleResetControls = () => {
      resetActuatorControls();
      const initialPosValues = {};
      actuatorList.forEach((actuator) => {
        if (!actuator?.joint) return;
        const jointUuid = resolveJointUuidFromRef(actuator.joint, joints);
        const jointEntry = jointUuid ? joints[jointUuid] : null;
        if (!jointEntry) return;
        const jointType = (actuator.kind || "").toLowerCase();
        if (jointType === "position" || jointType === "intposition" || jointType === "general") {
          const stateKey = getActuatorStateKey(actuator);
          if (stateKey) {
            initialPosValues[stateKey] = jointEntry.ref ?? 0;
          }
        }
      });
      mergeActuatorPositionTargets(initialPosValues);
    };

    window.addEventListener("physics-reset-controls", handleResetControls);
    return () => window.removeEventListener("physics-reset-controls", handleResetControls);
  }, [
    actuatorList,
    joints,
    getActuatorStateKey,
    mergeActuatorPositionTargets,
    resetActuatorControls,
  ]);

  const worldBodyMeta = React.useMemo(() => {
    if (!Array.isArray(worldBodies)) return [];
    return worldBodies
      .map((entry, index) => {
        if (!entry) return null;
        if (typeof entry === "string") {
          const fallbackName = bodies?.[entry]?.name || `Object ${index + 1}`;
          return {
            uuid: entry,
            name: fallbackName || `Object ${index + 1}`,
            order: index,
            rootBodyUuid: entry,
          };
        }
        const uuid = entry.uuid;
        if (!uuid) return null;
        const label = entry.name || bodies?.[uuid]?.name || `Object ${index + 1}`;
        const rootBodyUuid = entry.rootBodyUuid || entry.uuid;
        return {
          uuid,
          rootBodyUuid,
          name: label || `Object ${index + 1}`,
          order: index,
        };
      })
      .filter(Boolean);
  }, [worldBodies, bodies]);

  const groupedActuators = React.useMemo(() => {
    const orderLookup = new Map(
      worldBodyMeta.map((meta, idx) => [meta.rootBodyUuid || meta.uuid, idx])
    );
    const groups = new Map();
    const unassignedKey = "unassigned";
    const ensureGroup = (key, options = {}) => {
      if (!groups.has(key)) {
        const rootUuid = options.rootUuid;
        const metaIndex =
          typeof rootUuid === "string" && orderLookup.has(rootUuid)
            ? orderLookup.get(rootUuid)
            : undefined;
        const meta =
          typeof rootUuid === "string"
            ? worldBodyMeta.find((entry) => entry.uuid === rootUuid)
            : null;
        const title =
          options.title ||
          meta?.name ||
          (key === unassignedKey ? "Unassigned Actuators" : `Object ${groups.size + 1}`);
        const order =
          typeof metaIndex === "number"
            ? metaIndex
            : key === unassignedKey
              ? worldBodyMeta.length + 1
              : groups.size;
        groups.set(key, { key, title, motors: [], positions: [], order });
      }
      return groups.get(key);
    };

    worldBodyMeta.forEach((meta) => {
      const key = meta.rootBodyUuid || meta.uuid;
      ensureGroup(key, { title: meta.name, rootUuid: key });
    });

    actuatorList.forEach((actuator) => {
      if (!actuator) return;
      const jointUuid = resolveJointUuid(actuator);
      const jointMeta = jointUuid ? jointMetaMap.get(jointUuid) : null;
      let rootUuid = jointMeta?.rootUuid || null;

      if (!rootUuid && actuator.tendon) {
        rootUuid = tendonRootResolver(actuator);
      }

      const groupKey = rootUuid || unassignedKey;
      const group = ensureGroup(groupKey, { rootUuid });
      const kind = (actuator.kind || actuator.type || "").toLowerCase();
      const controlName = getActuatorStateKey(actuator);
      const label = actuator?.name || jointMeta?.joint?.name || actuator?.tendon || controlName;
      const entry = { actuator, controlName, label };

      if (SPEED_ACTUATOR_KINDS.has(kind)) group.motors.push(entry);
      else group.positions.push(entry);
    });

    ensureGroup(unassignedKey);

    return Array.from(groups.values()).sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      return a.title.localeCompare(b.title);
    });
  }, [
    actuatorList,
    worldBodyMeta,
    resolveJointUuid,
    jointMetaMap,
    getActuatorStateKey,
    tendonRootResolver,
  ]);

  const groupedJoints = React.useMemo(() => {
    const orderLookup = new Map(
      worldBodyMeta.map((meta, idx) => [meta.rootBodyUuid || meta.uuid, idx])
    );
    const groups = new Map();
    const unassignedKey = "unassigned";
    const ensureGroup = (key, options = {}) => {
      if (!groups.has(key)) {
        const rootUuid = options.rootUuid;
        const metaIndex =
          typeof rootUuid === "string" && orderLookup.has(rootUuid)
            ? orderLookup.get(rootUuid)
            : undefined;
        const meta =
          typeof rootUuid === "string"
            ? worldBodyMeta.find((entry) => entry.uuid === rootUuid)
            : null;
        const title =
          options.title ||
          meta?.name ||
          (key === unassignedKey ? "Unassigned Joints" : `Object ${groups.size + 1}`);
        const order =
          typeof metaIndex === "number"
            ? metaIndex
            : key === unassignedKey
              ? worldBodyMeta.length + 1
              : groups.size;
        groups.set(key, { key, title, joints: [], order });
      }
      return groups.get(key);
    };

    worldBodyMeta.forEach((meta) => {
      const key = meta.rootBodyUuid || meta.uuid;
      ensureGroup(key, { title: meta.name, rootUuid: key });
    });

    Object.entries(joints || {}).forEach(([jointUuid, joint]) => {
      if (!joint || (joint.kind !== "hinge" && joint.kind !== "slide")) return;
      const jointMeta = jointMetaMap.get(jointUuid);
      const rootUuid = jointMeta?.rootUuid || null;
      const groupKey = rootUuid || unassignedKey;
      const group = ensureGroup(groupKey, { rootUuid });
      group.joints.push({
        jointUuid,
        joint,
        label: joint?.name || jointUuid,
      });
    });

    ensureGroup(unassignedKey);

    return Array.from(groups.values()).sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      return a.title.localeCompare(b.title);
    });
  }, [joints, jointMetaMap, worldBodyMeta]);

  return {
    actuatorList,
    groupedActuators,
    groupedJoints,
    joints,
    motorValues,
    posValues,
    sceneActions,
    selectedJoint,
    setActuatorMotorControl,
    setActuatorPositionTarget,
  };
};
